import React, { useState, useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import styles from "./Notice.module.css"; // 스타일 파일 경로
import * as noticeFunctions from "../../../functions/Functions";
import { formatDateWbar } from "../../../components/Util/DateFormat";
import NoticeDetailPopup from "./Popup/NoticeDetailPopup";
import NoticeRegisterPopup from "./Popup/NoticeRegisterPopup";
import Swal from "sweetalert2";
import axios from "axios";
import { getServerBaseUrl } from "../../../utils/portConfig";

function Notice() {
  const [noticeData, setNoticeData] = useState([]); // 공지 데이터
  const [selectedRow, setSelectedRow] = useState(null); // 선택된 행
  const [isPopupOpen, setIsPopupOpen] = useState(false); // 팝업 열림 상태
  const [popupType, setPopupType] = useState(null); // 팝업 종류
  const [keyword, setKeyword] = useState(""); // 검색어
  const [showNoticePopup, setShowNoticePopup] = useState(false); // 공지 상세 정보 팝업 표시 여부
  const [popupData, setPopupData] = useState(null); // 팝업 데이터
  const [gridReload, setGridReload] = useState(false); // 그리드 다시 로드 플래그

  // 테이블 높이 설정값
  const [pageSize, setPageSize] = useState(10); // 기본값 10으로 시작
  const gridContainerRef = useRef(null);

  const gridRef = useRef(null);

  const API_SERVER_URL = `${getServerBaseUrl()}/api/v1/bbs`; // API 서버 URL
  const token = localStorage.getItem("token"); // 토큰
  const userId = localStorage.getItem("userid");

  // 화면이 그려질 때 사용자 데이터 조회
  useEffect(() => {
    getData(); // 초기 로드에서 검색 매개 변수 없이 호출
  }, [gridReload]);

  // 검색 버튼 클릭 시 실행되는 함수
  const handleSearch = () => {
    // 필터 상태 초기화
    const gridApi = gridRef.current.api;
    gridApi.setFilterModel(null);

    const searchParams = {
      keyword: keyword,
    };

    getData(searchParams);
  };

  // 데이터 가져오기 함수 추가
  const getData = async (params) => {
    try {
      // 데이터 로딩 인디케이터 표시
      Swal.fire({
        title: "데이터를 가져오는 중...",
        text: "잠시만 기다려 주세요.",
        didOpen: () => {
          Swal.showLoading();
        },
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
      });

      // API 호출 및 데이터 가져오기
      const response = await axios.get(`${API_SERVER_URL}/notice/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params, // 검색 매개 변수 전달
      });
      // 가져온 데이터를 상태에 설정
      setNoticeData(response.data);
    } catch (error) {
      console.error("Data fetch error:", error);
      Swal.fire({
        title: "오류!",
        text: "데이터를 가져오는 중 오류가 발생했습니다.",
        icon: "error",
        showConfirmButton: true, // 사용자가 확인 버튼을 눌러야 닫힙니다.
      });
    } finally {
      // 데이터 가져오기 완료 후 로딩 인디케이터 숨기기
      Swal.close();
    }
  };

  // 그리드 컬럼 정의
  const columns = [
    {
      field: "id",
      headerName: "번호",
      width: 130,
      suppressMenu: true,
    },
    {
      field: "title",
      headerName: "제목",
      cellStyle: { textAlign: "left", cursor: "pointer" },
      cellRenderer: cellRenderer,
      flex: 1,
    },
    {
      field: "regUser",
      headerName: "작성자",
      width: 130,
    },
    {
      field: "regDate",
      headerName: "작성일",
      valueFormatter: (params) => formatDateWbar(params.data.regDate),
      width: 130,
    },
    {
      field: "viewsCount",
      headerName: "조회 수",
      width: 130,
    },
  ];

  // 그리드 옵션
  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
    };
  }, []);

  // tooltip 기능
  function cellRenderer(params) {
    return (
      <span className="title-cell" title={params.value}>
        {params.value}
      </span>
    );
  }

  // row 선택 이벤트
  const selectRow = (event) => {
    noticeFunctions.selectRow(event, setSelectedRow);
  };

  // 생성 팝업 열기
  const OpenPopup = () => {
    noticeFunctions.OpenCreatePopup(setIsPopupOpen, setPopupType);
  };

  // 팝업 닫기
  const ClosePopup = () => {
    noticeFunctions.ClosePopup(setIsPopupOpen);
    if (gridRef.current) {
      gridRef.current.api.deselectAll();
      setSelectedRow(null);
    }
  };

  // 셀 클릭 이벤트 처리
  const onCellClicked = async (params) => {
    if (params.colDef.headerName === "제목") {
      try {
        const details = await fetchDetails(`${API_SERVER_URL}/${params.data.id}/${userId}`);
        if (!details) {
          // 상세 정보를 가져오지 못한 경우 오류 메시지 표시
          Swal.fire({
            title: "오류!",
            text: "데이터를 가져오는 중 오류가 발생했습니다.",
            icon: "error",
            showConfirmButton: true, // 사용자가 확인 버튼을 눌러야 닫힘
          });
          return;
        }

        setPopupData(details); // 상세 정보를 상태에 설정
        setShowNoticePopup(true); // 팝업 표시
      } catch (error) {
        console.error("데이터 가져오기 오류:", error);
        // 오류 처리를 원하는 대로 수행할 수 있습니다.
      }
    }
  };

  // 상세 정보 가져오기
  const fetchDetails = async (url) => {
    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status !== 200) {
        throw new Error("상세 정보를 가져오는 중 오류가 발생했습니다.");
      }

      return response.data;
    } catch (error) {
      console.error("상세 정보 fetch 오류:", error);
      return null;
    }
  };

  // 공지 등록
  const noticeSavePopup = async (noticeRegData) => {
    await noticeFunctions.createNotice(API_SERVER_URL, token, userId, noticeRegData, setIsPopupOpen, setGridReload);
  };

  // 공지 삭제
  const deleteNotice = async () => {
    await noticeFunctions.deleteNotice(selectedRow, token, setGridReload, setSelectedRow, setIsPopupOpen, setShowNoticePopup);
  };

  // 테이블 높이 변경 함수
  const calculatePageSize = () => {
    if (gridContainerRef.current) {
      const gridHeight = gridContainerRef.current.clientHeight;
      const rowHeight = 43; // AG Grid의 기본 행 높이 (alpine 테마)
      const headerHeight = 45; // 헤더 높이
      const paginationHeight = 45; // 페이지네이션 아래 여백

      const availableRows = Math.floor((gridHeight - headerHeight - paginationHeight) / rowHeight);

      // 최소 5개, 계산된 값보다 작은 행 수로 설정
      const newPageSize = Math.max(5, availableRows);
      setPageSize(newPageSize);
    }
  };

  // 컴포넌트 마운트 및 창 크기 변경 시 테이블 높이 조정
  useEffect(() => {
    calculatePageSize();

    // 창 크기 변경 이벤트 리스너 추가
    window.addEventListener("resize", calculatePageSize);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener("resize", calculatePageSize);
    };
  }, []);

  // 데이터 로드 후 그리드 크기에 맞게 테이블 높이 재계산
  useEffect(() => {
    if (noticeData.length > 0) {
      setTimeout(calculatePageSize, 100); // 데이터 로드 후 약간의 지연을 두고 계산
    }
  }, [noticeData]);

  return (
    <div className="dashboard-content">
      <div className="dashboard-content-container">
        <div className="dashboard-content-header">
          <h2>공지 관리</h2>
        </div>

        <div className={styles["search-container"]}>
          <div className={styles["search-group"]}></div>
          <div className="button-group">
            <button className={styles["gray-button"]} onClick={OpenPopup}>
              등록
            </button>
          </div>
        </div>

        {showNoticePopup && (
          <NoticeDetailPopup data={popupData} selectedRow={selectedRow} onDelete={deleteNotice} onClose={() => setShowNoticePopup(false)} token={token} userId={userId} getData={getData} />
        )}

        <NoticeRegisterPopup isOpen={isPopupOpen && popupType === "create"} onClose={ClosePopup} onSave={noticeSavePopup} />

        <div ref={gridContainerRef} className="ag-theme-alpine" style={{ height: "100%", width: "100%", fontWeight: "400" }}>
          <AgGridReact
            ref={(grid) => (gridRef.current = grid)}
            columnDefs={columns}
            pagination={true}
            paginationPageSize={pageSize}
            defaultColDef={defaultColDef}
            rowData={noticeData} // Ag-Grid의 데이터 소스
            onRowSelected={selectRow} // 행 선택 처리
            rowSelection="single" // 단일 행 선택
            domLayout="autoHeight"
            multiSortKey="shift"
            onCellClicked={onCellClicked}
          />
        </div>
      </div>
    </div>
  );
}

export default Notice;
